/**
 * Public Footer for landing pages - Black & Gold theme
 */
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Phone, Mail, Ticket } from 'lucide-react';
import { useEffect, useState } from 'react';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { Service } from '@/types';
import { MONKEY_FRANCHISE_ID } from '@/lib/config';

export function PublicFooter() {
  const { t } = useTranslation();
  const [services, setServices] = useState<Service[]>([]);

  useEffect(() => {
    const fetchServices = async () => {
      try {
        const q = query(
          collection(db, 'services'),
          where('franchiseId', '==', MONKEY_FRANCHISE_ID),
          where('isActive', '==', true)
        );
        const snapshot = await getDocs(q);
        const servicesList = snapshot.docs.map(doc => ({
          serviceId: doc.id,
          ...doc.data()
        })) as Service[];
        // Sort by displayOrder (set from admin) and take first 6
        setServices(servicesList.sort((a, b) => {
          const orderA = a.displayOrder ?? 999;
          const orderB = b.displayOrder ?? 999;
          if (orderA !== orderB) return orderA - orderB;
          return a.name.localeCompare(b.name);
        }).slice(0, 6));
      } catch (error) {
        console.error('Error fetching services for footer:', error);
      }
    };
    fetchServices();
  }, []);

  return (
    <footer className="bg-canvas border-t border-hairline py-12 transition-colors duration-300">
      <div className="container mx-auto px-6">
        <div className="max-w-6xl mx-auto grid md:grid-cols-5 gap-8">
          <div>
            <div className="flex items-center gap-2 mb-4">
              <img
                src='/logo/logo_monkey.svg'
                alt="Monkey Barber Logo"
                className='h-16 w-auto'
              />
              <div>
                <div className="font-display text-2xl text-accent">
                  MONKEY
                </div>
                <div className="text-sm text-ink-subtle uppercase tracking-wider">Barber Club</div>
              </div>
            </div>
            <p className="text-ink-muted text-sm">
              {t('home.footer.tagline')}
            </p>
          </div>

          <div className="md:col-span-2">
            <h4 className="font-bold mb-4 uppercase tracking-wider text-sm text-ink">{t('home.footer.sections.services')}</h4>
            {services.length > 0 ? (
              <div className="grid grid-cols-2 gap-x-6 gap-y-2 text-ink-muted text-sm">
                {services.map(service => (
                  <Link
                    key={service.serviceId}
                    to="/public-services"
                    className="hover:text-accent transition-colors flex justify-between items-center gap-2"
                  >
                    <span className="truncate">{service.name}</span>
                    <span className="text-ink font-medium tabular-nums whitespace-nowrap">{(service.price / 100)}€</span>
                  </Link>
                ))}
              </div>
            ) : (
              <ul className="space-y-2 text-ink-muted text-sm">
                <li><Link to="/public-services" className="hover:text-accent transition-colors">{t('home.footer.services.classic')}</Link></li>
                <li><Link to="/public-services" className="hover:text-accent transition-colors">{t('home.footer.services.combo')}</Link></li>
                <li><Link to="/public-services" className="hover:text-accent transition-colors">{t('home.footer.services.vip')}</Link></li>
              </ul>
            )}
            <Link
              to="/public-services"
              className="inline-block mt-4 text-accent hover:text-accent-hover text-sm font-medium transition-colors"
            >
              Ver todos los servicios →
            </Link>
          </div>

          <div>
            <h4 className="font-bold mb-4 uppercase tracking-wider text-sm text-ink">{t('home.footer.sections.company')}</h4>
            <ul className="space-y-2 text-ink-muted text-sm">
              <li><Link to="/login" className="hover:text-accent transition-colors">{t('home.footer.company.professional')}</Link></li>
              <li><Link to="/about" className="hover:text-accent transition-colors">{t('home.footer.company.about')}</Link></li>
              <li><Link to="/contact" className="hover:text-accent transition-colors">{t('home.footer.company.contact')}</Link></li>
            </ul>
          </div>

          <div>
            <h4 className="font-bold mb-4 uppercase tracking-wider text-sm text-ink">{t('home.footer.sections.contact')}</h4>
            <ul className="space-y-3 text-ink-muted text-sm">
              <li className="flex items-center gap-2">
                <Phone className="w-4 h-4 text-ink-subtle shrink-0" aria-hidden="true" />
                <a href="tel:+34604216330" className="hover:text-accent transition-colors">+34 604 216 330</a>
              </li>
              <li className="flex items-center gap-2">
                <Mail className="w-4 h-4 text-ink-subtle shrink-0" aria-hidden="true" />
                <a href="mailto:monkeybarbershop2@gmail.com" className="hover:text-accent transition-colors">monkeybarbershop2@gmail.com</a>
              </li>
              <li className="flex items-center gap-2">
                <Ticket className="w-4 h-4 text-ink-subtle shrink-0" aria-hidden="true" />
                <Link to="/login" className="hover:text-accent transition-colors">
                  Reservar
                </Link>
              </li>
            </ul>
          </div>
        </div>

        <div className="border-t border-hairline mt-12 pt-8">
          <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
            <p className="text-ink-subtle text-sm">
              {t('home.footer.copyright')}
            </p>
            <div className="flex flex-wrap justify-center gap-4 text-sm">
              <Link to="/privacy-policy" className="text-ink-subtle hover:text-accent transition-colors">
                {t('cookies.privacyPolicy')}
              </Link>
              <Link to="/cookies-policy" className="text-ink-subtle hover:text-accent transition-colors">
                {t('cookies.cookiesPolicy')}
              </Link>
              <Link to="/terms-of-service" className="text-ink-subtle hover:text-accent transition-colors">
                {t('cookies.termsOfService')}
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
